/******/ (() => { // webpackBootstrap
// Shared across all cart-quantity elements: lets late responses detect they are stale
let cartRequestSeq = 0;
const pendingQuantityControls = new Set();
const activeQuantityUpdates = new Set();
let quantityQueue = Promise.resolve();
let quantityFailure = null;
window.lspFlushQuantityUpdates = async () => {
  for (const control of [...pendingQuantityControls]) control.flushQuantityUpdate();
  while (activeQuantityUpdates.size) await Promise.all([...activeQuantityUpdates]);
  if (quantityFailure) throw quantityFailure;
};
class CartQuantity extends HTMLElement {
  constructor() {
    super();
    this.removeButton = this.querySelector('[data-cart-remove-item]');
    this.adjustButtons = this.querySelectorAll('[data-cart-quantity-button]');
    this.input = this.querySelector('[data-cart-quantity-input]');
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleRemoveItem = this.handleRemoveItem.bind(this);
    this.updateCart = this.updateCart.bind(this);
    this.updateLineItem = this.updateLineItem.bind(this);
    this.updateCartCountIndicator = this.updateCartCountIndicator.bind(this);
    this.handleCartErrors = this.handleCartErrors.bind(this);
    this.setLoadingState = this.setLoadingState.bind(this);
    this.hideLoadingState = this.hideLoadingState.bind(this);
    this.setRemoveLoadingState = this.setRemoveLoadingState.bind(this);
    this.hideRemoveLoadingState = this.hideRemoveLoadingState.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-cart-quantity'});
    this.removeButton?.addEventListener('click', this.handleRemoveItem);
    this.adjustButtons.forEach(button => button.addEventListener('click', this.handleButtonClick));
    this.input?.addEventListener('change', this.handleInputChange);
  }

  handleButtonClick(e) {
    /* ===== Handle the button click event ===== */
    e.preventDefault();
    const previousValue = this.input.value;
    const adjustment = e.currentTarget.name === 'plus' ? 1 : -1;

    // Adjust the quantity by the adjustment value
    this.adjustQuantity(adjustment);

    // Process the new quantity if quantity has changed
    if (previousValue !== this.input.value) {
      this.handleQuantityChange(this.input.value);
    }
  }

  adjustQuantity(adjustment) {
    /* ===== Adjust the quantity by the adjustment value ===== */
    const newValue = parseInt(this.input.value) + adjustment;
    // If the new value is not a number, set it to 1
    this.input.value = isNaN(newValue) ? 1 : newValue;

    // If the value is smaller than the min attribute, set it to the min attribute
    if (this.input.min && parseInt(this.input.value) < parseInt(this.input.min)) {
      this.input.value = this.input.min;
    }
  }

  handleInputChange(e) {
    /* ===== Handle the change event ===== */
    e.preventDefault();

    // If the value is not a number, set it to 1
    if (isNaN(this.input.value)) {
      this.input.value = 1;
    }

    // If the value is smaller than the min attribute, set it to the min attribute
    if (this.input.min && parseInt(this.input.value) < parseInt(this.input.min)) {
      this.input.value = this.input.min;
    }

    // Process the new quantity
    this.handleQuantityChange(this.input.value);
  }

  handleQuantityChange(inputValue) {
    /* ===== Update the cart with the new quantity (debounced: rapid +/- clicks
       collapse into one request instead of racing each other) ===== */
    const quantity = Number(inputValue);
    if (!Number.isSafeInteger(quantity) || quantity < 0 || String(inputValue).trim() === '') {
      this.handleCartErrors(this.itemIndex, 'Enter a whole-number quantity of zero or more.');
      return;
    }
    clearTimeout(this.quantityDebounce);
    this.setLoadingState();
    this.pendingQuantity = { line: this.itemTarget, quantity };
    pendingQuantityControls.add(this);
    this.quantityDebounce = setTimeout(() => this.flushQuantityUpdate().catch(() => {}), 300);
  }

  flushQuantityUpdate() {
    clearTimeout(this.quantityDebounce);
    pendingQuantityControls.delete(this);
    const pending = this.pendingQuantity;
    this.pendingQuantity = null;
    return pending ? this.updateCart(pending.line, pending.quantity) : Promise.resolve();
  }

  handleRemoveItem() {
    /* ===== Set quantity of line item to zero ===== */
    clearTimeout(this.quantityDebounce);
    pendingQuantityControls.delete(this);
    this.pendingQuantity = null;
    this.updateCart(this.itemTarget, 0).catch(() => {});
  }

  updateCart(line, quantity) {
    const task = quantityQueue.then(() => {
      quantityFailure = null;
      const run = window.bagRunMutation || (fn => fn());
      return run(() => this.performCartUpdate(line, quantity));
    });
    quantityQueue = task.catch(() => {});
    activeQuantityUpdates.add(task);
    task.then(() => activeQuantityUpdates.delete(task), error => {
      activeQuantityUpdates.delete(task);
      quantityFailure = error;
    });
    return task;
  }

  async performCartUpdate(line, quantity) {
    const seq = ++cartRequestSeq;
    try {
      /* ===== Set the loading state ===== */
      this.setLoadingState();

      /* ===== If quantity is 0, set the 'remove' loading state ===== */
      if (quantity === 0) {
        this.setRemoveLoadingState();
      }

      /* ===== Update the line item quantity ===== */
      let response = await this.updateLineItem(line, quantity);
      if (!response) return;

      /* ===== If successful response and has gift wrap, update the gift wrap product quantity ===== */
      if (this.hasGiftWrap) {
        const adjustment = quantity - this.previousQuantity;
        const cart = await this.getCart();
        if (this.getGiftWrapLine(cart) && this.getGiftWrapQuantity(cart)) {
          const giftWrapQuantity = Math.max(0, this.getGiftWrapQuantity(cart) + adjustment);
          response = await this.updateLineItem(this.getGiftWrapLine(cart), giftWrapQuantity, false);
        }
      }
      if (!response) return;

      /* ===== Personalization add-ons must track their robe quantity ===== */
      const lspResponse = await window.lspReconcileAddons?.(true);
      if (lspResponse && lspResponse.sections) response = lspResponse;

      /* ===== Drop stale responses: a newer request has already been issued ===== */
      if (seq !== cartRequestSeq) return;

      /* ===== If successful response, re-render the cart ===== */
      window.eventBus.emit('update:cart:drawer', response);

      /* ===== Update cart count ===== */
      this.updateCartCountIndicator();
    } catch (error) {
      console.error('Error updating cart:', error);
      this.handleCartErrors(line, error?.message);
      throw error;
    } finally {
      // Successful updates replace this element with freshly rendered markup.
      // Failed/no-op requests keep it connected and must always clear loaders.
      if (this.isConnected) {
        this.hideLoadingState();
        this.hideRemoveLoadingState();
      }
    }
  }

  async updateLineItem(line, quantity, resolveItem = true) {
    try {
      /* ===== Update line item quantity ===== */
      let lineNumber = Number.parseInt(line, 10);
      if (resolveItem && this.dataset.itemVariant) {
        const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('We could not read the current cart. Please retry.');
        const cart = await response.json();
        const rawProperties = JSON.parse(this.dataset.itemProperties || '{}');
        const properties = Array.isArray(rawProperties)
          ? Object.fromEntries(rawProperties)
          : rawProperties;
        const sameProperties = candidate => {
          const actual = candidate.properties || {};
          return Object.keys(properties).length === Object.keys(actual).length && Object.entries(properties).every(([key, value]) => actual[key] === value);
        };
        lineNumber = cart.items.findIndex(item => Number(item.variant_id) === Number(this.dataset.itemVariant) && sameProperties(item) && String(item.selling_plan_allocation?.selling_plan?.id || '') === (this.dataset.itemSellingPlan || '')) + 1;
      }
      if (!Number.isInteger(lineNumber) || lineNumber < 1) {
        throw new Error('This cart item could not be identified. Please refresh and try again.');
      }
      const body = JSON.stringify({
        // Shopify's drawer endpoint consistently accepts the one-based line index.
        // The previous `id: item.key` request was rejected by /cart/change.
        line: lineNumber,
        quantity,
        sections: 'cart-drawer'
      });

      const response = await fetch(window.routes.cart_change_url, { 
        ...this.fetchConfig(),
        body
      });

      const responseText = await response.text();

      if (!responseText) {
        throw new Error('Shopify returned an empty cart response. Please try again.');
      }

      let parsedResponse;
      try {
        parsedResponse = JSON.parse(responseText);
      } catch (_error) {
        throw new Error('Shopify returned an invalid cart response. Please try again.');
      }

      /* ===== Handle errors ===== */
      if (!response.ok || parsedResponse.errors || parsedResponse.status >= 400) {
        const message = parsedResponse.description || parsedResponse.message || parsedResponse.errors;
        throw new Error(typeof message === 'string' ? message : 'Something went wrong updating your cart. Please try again.');
      }

      return parsedResponse;
    } catch (error) {
      console.error('Error updating cart line item:', error);
      throw error;
    }
  }

  fetchConfig(type = 'json') {
    /* ===== Create the fetch configuration object ===== */
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  handleCartErrors(line, errors) {
    /* ===== Reset loading state and input value ===== */
    this.hideLoadingState();
    this.hideRemoveLoadingState();
    this.input.value = this.input.getAttribute('value');

    /* ===== Display the error message ===== */
    this.renderErrorMessage(line, errors);
  }

  renderErrorMessage(line, message) {
    /* ===== Display the error message on this line (fall back to own index) ===== */
    const errorElement = document.querySelector(`[data-cart-error-index="${line}"]`)
      || document.querySelector(`[data-cart-error-index="${this.itemIndex}"]`);
    if (!errorElement) return;
    let text = message;
    if (typeof text !== 'string' || !text) text = 'Something went wrong updating your cart. Please try again.';
    errorElement.textContent = text;
    errorElement.setAttribute('aria-hidden', false);

    /* ===== If we're at the bottom of the cart, scroll error message into view ===== */
    const isLastItem = errorElement.getAttribute('data-cart-is-last-item') === 'true';
    if (isLastItem) {
      errorElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  async getCart() {
    /* ===== Fetch the cart information ===== */
    try {
      const response = await fetch(`${window.routes.cart_url}?view=compare`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching cart: ${error.description || error.message}`);
    }
  }

  updateCartCountIndicator() {
    /* ===== Update the cart count indicator ===== */
    if (!this.cartCountIndicator) return;
    this.getCart().then(cart => window.wetheme.updateCartCount(cart));
  }

  getGiftWrapLine(cart) {
    return cart.items.findIndex(item => item.variant_id === this.giftWrapVariantId) + 1 || null;
  }

  getGiftWrapQuantity(cart) {
    return cart.items.find(item => item.variant_id === this.giftWrapVariantId)?.quantity || null;
  }

  setLoadingState() {
    this.classList.add('is-loading');
  }

  hideLoadingState() {
    this.classList.remove('is-loading');
  }

  setRemoveLoadingState() {
    this.classList.add('is-removing');
  }

  hideRemoveLoadingState() {
    this.classList.remove('is-removing');
  }

  get itemIndex() {
    return parseInt(this.getAttribute('data-item-index'));
  }

  get itemTarget() {
    return this.itemIndex;
  }

  get previousQuantity() {
    return parseInt(this.getAttribute('data-item-quantity'));
  }

  get hasGiftWrap() {
    return this.getAttribute('data-has-gift-wrap') === "true";
  }

  get giftWrapVariantId() {
    return parseInt(this.getAttribute('data-gift-wrap-variant-id'));
  }
}

// This file can be loaded by both the cart page and cart drawer bundles.
if (!customElements.get('cart-quantity')) {
  customElements.define('cart-quantity', CartQuantity);
}
/******/ })()
;

// --- Love Spell personalization add-on reconciler -----------------------------
// Invariant: every `_Love Spell add-on` line's quantity equals the summed quantity
// of robe lines sharing its `_Personalization group`; orphaned add-ons are removed.
// Runs after drawer updates, on page load, and as a final gate before checkout, so
// a personalized robe can never ship with an unpaid (or overpaid) personalization.
(() => {
  const GROUP = '_Personalization group';
  const FEE_FLAGS = ['_Personalization fee', '_Love Spell add-on', '_Pajama personalization add-on'];
  const isFee = (props) => FEE_FLAGS.some((flag) => props[flag] === 'true');
  const cartJsonUrl = () => window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
  const cartAddUrl = () => window.routes?.cart_add_url || '/cart/add.js';
  const cartUpdateUrl = () => window.routes?.cart_update_url || '/cart/update.js';
  let inFlight = null;

  const getCartCoordinator = async (requiredMethods = ['desiredFeeQuantity']) => {
    for (let attempt = 0; attempt < 50; attempt += 1) {
      const coordinator = window.BlossomPersonalizationCart;
      if (coordinator && requiredMethods.every((method) => typeof coordinator[method] === 'function')) return coordinator;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    throw new Error('Cart verification could not load. Please refresh and try again.');
  };

  window.lspReconcileAddons = function lspReconcileAddons(renderSections = false) {
    if (inFlight) return inFlight;
    inFlight = (async () => {
      try {
        const coordinator = await getCartCoordinator();
        const cartResponse = await fetch(cartJsonUrl(), { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('We could not read your cart.');
        const cart = await cartResponse.json();
        const parents = new Map();
        const addOnVariants = new Map();
        for (const item of cart.items) {
          const props = item.properties || {};
          if (isFee(props) || !props[GROUP]) continue;
          parents.set(props[GROUP], item);
          const variantId = Number(props['_Personalization add-on variant']);
          if (variantId) addOnVariants.set(props[GROUP], variantId);
        }
        const updates = {};
        const existingAddOnGroups = new Set();
        for (const item of cart.items) {
          const props = item.properties || {};
          if (!isFee(props)) continue;
          existingAddOnGroups.add(props[GROUP]);
          const parent = parents.get(props[GROUP]);
          const parentProps = parent?.properties || {};
          const want = parent
            ? coordinator.desiredFeeQuantity(parent, { format: props['_Pajama personalization add-on'] === 'true' ? 'pajama' : 'shared' })
            : 0;
          if (parent && want <= 0) throw new Error('This older personalization needs to be configured again before checkout.');
          if (item.quantity !== want) updates[item.key] = want;
        }
        const missingAddOns = [...parents.entries()]
          .filter(([group, parent]) => parent.quantity > 0 && addOnVariants.has(group) && !existingAddOnGroups.has(group))
          .map(([group, parent]) => {
            const parentProps = parent.properties || {};
            const shared = parentProps['_Personalization schema'] === '1';
            const pajama = !shared && addOnVariants.get(group) === 45146667843782;
            const quantity = coordinator.desiredFeeQuantity(parent, { format: pajama ? 'pajama' : 'shared' });
            if (quantity <= 0) throw new Error('This older personalization needs to be configured again before checkout.');
            return {
              id: addOnVariants.get(group),
              quantity,
              properties: shared
                ? {
                    '_Personalization fee': 'true',
                    [GROUP]: group,
                    '_Associated garment': String(parent.variant_id),
                    '_Personalization fee key': [parentProps['_Personalization profile'], parentProps['_Personalization choice']].filter(Boolean).join('_'),
                    '_Personalization batch': parentProps['_Personalization batch'] || '',
                  }
                : {
                    [GROUP]: group,
                    [pajama ? '_Pajama personalization add-on' : '_Love Spell add-on']: 'true',
                    '_Associated robe': 'Love Spell personalization',
                  },
            };
          });
        if (!Object.keys(updates).length && !missingAddOns.length) return null;
        let result = null;
        if (Object.keys(updates).length) {
          const body = { updates };
          if (renderSections && !missingAddOns.length) body.sections = 'cart-drawer';
          const response = await fetch(cartUpdateUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          });
          if (!response.ok) throw new Error('We could not update the personalization fee. Please try again.');
          result = await response.json();
        }
        if (missingAddOns.length) {
          const body = { items: missingAddOns };
          if (renderSections) body.sections = 'cart-drawer';
          const response = await fetch(cartAddUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          });
          if (!response.ok) throw new Error('We could not restore the personalization fee. Please try again.');
          result = await response.json();
        }
        return result;
      } catch (error) {
        console.error('Love Spell add-on sync failed:', error);
        throw error;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  };

  // Load-time sweep: repairs any desync left by other surfaces (e.g. the /cart page).
  const sweep = async () => {
    const fixed = await window.lspReconcileAddons();
    if (fixed && window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
      window.location.reload();
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep, { once: true });
  } else {
    sweep();
  }

  // Checkout gate: whatever surface changed quantities, fix the cart before it submits.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    let actionPath = action;
    try { actionPath = new URL(action, window.location.origin).pathname; } catch (_) { /* fall back to the raw action */ }
    if (!/(^|\/)cart\/?$/.test(actionPath)) return;
    if (form.dataset.lspSynced === 'true') {
      delete form.dataset.lspSynced;
      return;
    }
    const submitter = event.submitter;
    event.preventDefault();
    if (form.dataset.lspChecking === 'true') return;
    form.dataset.lspChecking = 'true';
    (async () => {
      try {
        const coordinator = await getCartCoordinator(['applyQuantityEdits', 'validateCheckoutCart']);
        await window.lspFlushQuantityUpdates?.();
        await window.bagFlushExtras?.();
        const quantityInputs = [...form.querySelectorAll('[data-cart-line-key]')];
        const edits = quantityInputs.filter(input => input.value !== input.defaultValue).map(input => ({ key: input.dataset.cartLineKey, value: input.value }));
        await coordinator.applyQuantityEdits(edits);
        await window.lspReconcileAddons();
        if (submitter?.name === 'update') {
          window.location.assign(window.routes?.cart_url || '/cart');
          return;
        }
        const cartResponse = await fetch(cartJsonUrl(), { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('We could not verify your cart.');
        const cart = await cartResponse.json();
        const validation = coordinator.validateCheckoutCart(cart);
        if (!validation.valid) throw new Error(validation.issues.join(' '));
        // The native POST must not reapply stale fee quantities after reconciliation.
        quantityInputs.forEach(input => { input.disabled = true; });
        form.dataset.lspSynced = 'true';
        if (submitter) form.requestSubmit(submitter);
        else form.requestSubmit();
      } catch (error) {
        const message = error?.message || 'We could not verify personalization. Please try again.';
        const errorBox = document.querySelector('[data-cart-errors], [data-builder-error], .cart-errors');
        if (errorBox) { errorBox.textContent = message; errorBox.hidden = false; }
        else window.alert(message);
      } finally {
        delete form.dataset.lspChecking;
      }
    })();
  }, true);
})();
